/**
 * RemoveAccountSection Component
 * Multi-step account deletion flow:
 *   1. Confirmation → triggers OTP email
 *   2. OTP verification dialog
 *   3. Feedback/reason dialog → final deletion
 *   4. Success dialog → logout + redirect
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import { useDeleteAccount, useSendDeletionOtp, useVerifyDeletionOtp } from "../hooks/useSettings"

type Step = "confirm" | "otp" | "feedback" | "success"

export function RemoveAccountSection() {
  const { t } = useTranslation("settings")
  const [step, setStep] = useState<Step>("confirm")

  // Screen 2 state
  const [otpValue, setOtpValue] = useState("")
  const [otpError, setOtpError] = useState("")
  const [resendCooldown, setResendCooldown] = useState(0)
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Screen 3 state
  const [feedback, setFeedback] = useState("")
  const [feedbackError, setFeedbackError] = useState("")

  // Hooks
  const { mutate: deleteAccount, isPending: isDeleting } = useDeleteAccount()
  const { mutate: sendOtp, isPending: isSendingOtp } = useSendDeletionOtp()
  const { mutate: verifyOtp, isPending: isVerifyingOtp } = useVerifyDeletionOtp()
  const logout = useAuthStore(state => state.logout)
  const userEmail = useAuthStore(state => state.user?.email ?? "")

  // Start 60s resend cooldown
  const startCooldown = useCallback(() => {
    setResendCooldown(60)
    if (cooldownRef.current) clearInterval(cooldownRef.current)
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  // Cleanup cooldown on unmount
  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current)
    }
  }, [])

  // ── Reset to Screen 1 ──────────────────────────────────────────────────

  const resetToStep1 = useCallback(() => {
    setStep("confirm")
    setOtpValue("")
    setOtpError("")
    setFeedback("")
    setFeedbackError("")
    if (cooldownRef.current) clearInterval(cooldownRef.current)
    setResendCooldown(0)
  }, [])

  // ── Screen 1: Confirmation → sends OTP ────────────────────────────────

  const handleConfirmRemove = () => {
    sendOtp(undefined, {
      onSuccess: () => {
        startCooldown()
        setStep("otp")
      },
    })
  }

  // ── Screen 2: OTP Verification ────────────────────────────────────────

  const handleOtpComplete = (_value: string) => {
    setOtpError("")
    // Auto-advance when all digits are entered
  }

  const handleOtpConfirm = () => {
    if (otpValue.length < 6) {
      setOtpError(t("removeAccount.otpRequired"))
      return
    }
    setOtpError("")
    verifyOtp(otpValue, {
      onSuccess: () => {
        setStep("feedback")
      },
      onError: (error: unknown) => {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Invalid verification code"
        setOtpError(message)
      },
    })
  }

  const handleOtpCancel = () => {
    resetToStep1()
  }

  const handleResendOtp = () => {
    if (resendCooldown > 0) return
    setOtpValue("")
    setOtpError("")
    sendOtp(undefined, {
      onSuccess: () => {
        startCooldown()
      },
    })
  }

  // ── Screen 3: Feedback + Final Deletion ───────────────────────────────

  const handleFinalDelete = () => {
    deleteAccount(
      { otp: otpValue, feedback: feedback.trim() || undefined },
      {
        onSuccess: () => {
          setStep("success")
        },
        onError: (error: unknown) => {
          const message =
            (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            "Failed to delete account"
          setFeedbackError(message)
        },
      },
    )
  }

  const handleFeedbackCancel = () => {
    resetToStep1()
  }

  // ── Screen 4: Success ─────────────────────────────────────────────────

  const handleReturnHome = async () => {
    try {
      await logout()
    } catch {
      // Already deleted, just navigate
    }
    window.location.href = "/"
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-7.5">
      {/* Section Header — always visible in content panel */}
      <div>
        <h1 className="text-xl font-semibold leading-5 text-primary uppercase section-heading">
          {t("removeAccount.title")}
        </h1>
        <p className="text-sm text-primary leading-[25px] mt-2.5 md:mt-1.25">
          {t("removeAccount.description")}
        </p>
      </div>

      {/* ─── Screen 1: Confirmation ─── */}
      {step === "confirm" && (
        <div className="flex flex-col gap-5">
          <p className="text-[15px] text-primary leading-[25px] font-semibold">
            {t("removeAccount.descriptionConfirmStep2")}
          </p>

          <Button
            variant="primary-terracotta"
            size="md"
            onClick={handleConfirmRemove}
            isLoading={isSendingOtp}
            className="uppercase w-[220px] h-[50px]"
          >
            {t("removeAccount.remove")}
          </Button>
        </div>
      )}

      {/* ─── Screen 2: OTP Verification Dialog ─── */}
      <Dialog
        open={step === "otp"}
        onOpenChange={open => {
          if (!open) handleOtpCancel()
        }}
      >
        <DialogContent
          showCloseButton={false}
          overlayClass="bg-[#020202]/90"
          className="md:w-[600px] w-[calc(100%-2rem)] bg-brand-terracotta/5 backdrop-blur-[50px] border-brand-terracotta/50 !rounded-none !rounded-tr-[30px] !rounded-br-[30px] gap-0 pt-8 pb-6 px-6 md:pt-10 md:pb-[30px]"
        >
          {/* Close button — frosted circle */}
          <button
            type="button"
            onClick={handleOtpCancel}
            aria-label="Close"
            className="absolute right-[20px] top-[20px] size-10 rounded-full bg-primary/5 border border-primary/15 backdrop-blur-sm flex items-center justify-center cursor-pointer border-gradient-3d"
          >
            <svg
              className="size-5 text-primary"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* Title */}
          <h2 className="text-primary/90 text-xl md:text-2xl font-semibold text-center leading-[29px] mb-[10px]">
            {t("removeAccount.otpTitle")}
          </h2>

          {/* Description with actual email in semibold */}
          <p className="text-primary text-sm font-normal text-center leading-[25px] mb-5 md:mb-[20px]">
            {t("removeAccount.otpDescription")} <span className="font-semibold">{userEmail}</span>{" "}
            {t("removeAccount.otpDescriptionEnd")}
          </p>

          {/* OTP Input — bottom-border only slots, centered */}
          <div className="flex justify-center mb-[15px]">
            <InputOTP
              maxLength={6}
              value={otpValue}
              onChange={(newValue: string) => {
                setOtpValue(newValue)
                setOtpError("")
              }}
              onComplete={handleOtpComplete}
              autoFocus
              inputMode="numeric"
              pasteTransformer={(pasted: string) => pasted.replace(/\s/g, "")}
              containerClassName="flex items-center justify-center gap-3 md:gap-[20px] has-disabled:opacity-50"
            >
              <InputOTPGroup className="flex items-center gap-3 md:gap-[20px]">
                {Array.from({ length: 6 }).map((_, index) => (
                  <InputOTPSlot
                    key={`otp-slot-${index}`}
                    index={index}
                    className="!size-10 md:!size-[60px] !rounded-none !border-0 !border-b !border-primary/20 !shadow-none text-primary text-lg md:text-2xl font-semibold !ring-0 !outline-none data-[active=true]:!ring-0 data-[active=true]:!border-b-2 data-[active=true]:!border-brand-terracotta"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          {otpError && <p className="text-xs text-primary/80 text-center mb-[10px]">{otpError}</p>}

          {/* Resend text */}
          <p className="text-primary text-sm font-normal text-center leading-[25px] mb-5 md:mb-[20px]">
            {t("removeAccount.resendPrefix")}{" "}
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendCooldown > 0 || isSendingOtp}
              className="font-semibold underline hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {resendCooldown > 0
                ? t("removeAccount.resendIn", { seconds: resendCooldown })
                : t("removeAccount.resendAction")}
            </button>
          </p>

          {/* Buttons — Cancel (left) + Continue (right), centered */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="outline"
              size="md"
              onClick={handleOtpCancel}
              className="!rounded-full !bg-transparent !border-primary !text-primary hover:!bg-primary hover:!text-primary uppercase w-full sm:w-[270px] h-[50px]"
            >
              {t("removeAccount.cancel")}
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleOtpConfirm}
              isLoading={isVerifyingOtp}
              className="!rounded-full !bg-transparent !border-brand-terracotta !text-brand-terracotta hover:!text-brand-terracotta uppercase w-full sm:w-[270px] h-[50px]"
            >
              {t("removeAccount.confirm")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Screen 3: Feedback/Reason Dialog ─── */}
      <Dialog
        open={step === "feedback"}
        onOpenChange={open => {
          if (!open) handleFeedbackCancel()
        }}
      >
        <DialogContent
          showCloseButton={false}
          overlayClass="bg-[#020202]/90"
          className="md:w-[600px] w-[calc(100%-2rem)] bg-brand-terracotta/5 backdrop-blur-[50px] border-brand-terracotta/50 !rounded-none !rounded-tr-[30px] !rounded-br-[30px] gap-0 pt-8 pb-6 px-6 md:pt-10 md:pb-[30px]"
        >
          {/* Close button — frosted circle */}
          <button
            type="button"
            onClick={handleFeedbackCancel}
            aria-label="Close"
            className="absolute right-[20px] top-[20px] size-10 rounded-full bg-primary/5 border border-primary/15 backdrop-blur-sm flex items-center justify-center cursor-pointer border-gradient-3d"
          >
            <svg
              className="size-5 text-primary"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* Title */}
          <h2 className="text-primary/90 text-xl md:text-2xl font-semibold text-center leading-[29px] mb-[10px]">
            {t("removeAccount.feedbackTitle")}
          </h2>

          {/* Description */}
          <p className="text-primary text-sm font-normal text-center leading-[25px] mb-5 md:mb-[20px]">
            {t("removeAccount.feedbackDescriptionLine1")}
            <br />
            {t("removeAccount.feedbackDescriptionLine2")}
          </p>

          {/* Textarea — transparent bg, off-white border */}
          <div className="flex justify-center mb-5 md:mb-[25px]">
            <textarea
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              placeholder={t("removeAccount.feedbackPlaceholder")}
              className="bg-transparent text-[#F2F0EB] caret-[#F2F0EB] border border-white/15 rounded-[15px] min-h-[151px] w-full max-w-[560px] shadow-none placeholder:text-[#F2F0EB80] ring-0 focus-visible:ring-0 focus-visible:border-white/25 outline-none px-5 py-5 text-sm font-normal resize-none field-sizing-content"
            />
          </div>

          {/* Warning text */}
          <p className="text-sm text-primary font-semibold text-center leading-[25px] max-w-[448px] mx-auto mb-5 md:mb-[25px]">
            {t("removeAccount.feedbackWarning")}
          </p>
          {feedbackError && (
            <p className="text-xs text-primary/80 text-center mb-[10px]">{feedbackError}</p>
          )}

          {/* Buttons — Cancel (left) + Remove Account (right) */}
          <div className="flex flex-col sm:flex-row gap-2  justify-center">
            <Button
              variant="outline"
              size="md"
              onClick={handleFeedbackCancel}
              className="!rounded-full !bg-transparent !border-primary !text-primary hover:!bg-primary hover:!text-primary uppercase w-full sm:w-[275px] h-[50px]"
            >
              {t("removeAccount.cancel")}
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleFinalDelete}
              isLoading={isDeleting}
              className="!rounded-full !bg-transparent !border-brand-terracotta !text-brand-terracotta hover:!text-primary uppercase w-full sm:w-[275px] h-[50px]"
            >
              {t("removeAccount.remove")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Screen 4: Success Dialog ─── */}
      <Dialog open={step === "success"} onOpenChange={() => {}}>
        <DialogContent
          showCloseButton={false}
          overlayClass="bg-brand-dark/90"
          className="md:w-[520px] bg-brand-dark border-brand-offwhite/30 gap-7.5 pt-[50px] pb-[40px] px-[50px]"
        >
          <DialogHeader className="text-center gap-2.5">
            <DialogTitle className="text-primary/90 text-2xl font-semibold text-center leading-6">
              {t("removeAccount.successTitle")}
            </DialogTitle>
            <DialogDescription className="text-primary text-center text-sm font-normal leading-6">
              {t("removeAccount.successDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center">
            <Button
              variant="primary-white"
              size="md"
              onClick={handleReturnHome}
              className="uppercase min-w-[280px]"
            >
              {t("removeAccount.returnHome")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
